import React, { useState, useEffect } from 'react';
import { useClientAuth } from '../contexts/ClientAuthContext';
import { useNavigate } from 'react-router-dom';
import { LogOut, FileText, User, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientPortal = () => {
  const { client, logout, getAuthHeader } = useClientAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      const response = await axios.get(
        `${API}/client/my-consultations`,
        { headers: getAuthHeader() }
      );
      setConsultations(response.data);
    } catch (error) {
      console.error('Failed to fetch consultations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'status-new';
      case 'contacted': return 'status-contacted';
      case 'in_progress': return 'status-progress';
      case 'completed': return 'status-completed';
      default: return '';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'new': return <Clock size={16} />;
      case 'contacted': return <FileText size={16} />;
      case 'in_progress': return <FileText size={16} />;
      case 'completed': return <CheckCircle size={16} />;
      default: return null;
    }
  };

  return (
    <div className="client-portal">
      {/* Header */}
      <header className="client-header">
        <div className="container">
          <div className="client-header-content">
            <div>
              <h1 className="heading-2">VISAASURE</h1>
              <p className="body-small">Client Portal</p>
            </div>
            <div className="client-header-actions">
              <div className="client-info">
                <User size={20} />
                <span className="body-small">{client?.name}</span>
              </div>
              <button onClick={handleLogout} className="btn-secondary">
                <LogOut size={20} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="client-main">
        <div className="container">
          <div className="client-welcome">
            <h2 className="heading-1">Welcome back, {client?.name}!</h2>
            <p className="body-large">Track your visa consultation requests and application status</p>
          </div>

          {/* Consultations */}
          <div className="consultations-section">
            <h3 className="heading-2">Your Consultation Requests</h3>
            
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading your consultations...</p>
              </div>
            ) : consultations.length === 0 ? (
              <div className="empty-state">
                <FileText size={48} />
                <h4 className="heading-3">No Consultation Requests Yet</h4>
                <p className="body-medium">Submit a consultation request from the home page to get started</p>
                <button onClick={() => navigate('/')} className="btn-primary">
                  Go to Home Page
                </button>
              </div>
            ) : (
              <div className="consultations-grid">
                {consultations.map((consultation) => (
                  <div key={consultation.id} className="consultation-card">
                    <div className="consultation-header">
                      <h4 className="heading-3">{consultation.visa_type}</h4>
                      <span className={`status-badge ${getStatusColor(consultation.status)}`}>
                        {getStatusIcon(consultation.status)}
                        <span>{consultation.status.replace('_', ' ')}</span>
                      </span>
                    </div>
                    <div className="consultation-details">
                      <p className="body-small">
                        <strong>Country:</strong> {consultation.country}
                      </p>
                      <p className="body-small">
                        <strong>Travel Date:</strong> {consultation.travel_date}
                      </p>
                      <p className="body-small">
                        <strong>Number of Applicants:</strong> {consultation.num_applicants}
                      </p>
                      <p className="body-small">
                        <strong>Email:</strong> {consultation.email}
                      </p>
                      <p className="body-small">
                        <strong>Phone:</strong> {consultation.phone}
                      </p>
                      {consultation.message && (
                        <p className="body-small">
                          <strong>Message:</strong> {consultation.message}
                        </p>
                      )}
                      <p className="caption">
                        Submitted: {new Date(consultation.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ClientPortal;